// İSG Portal — platform-agnostik depolama soyutlaması.
//
// `packages/shared` hem web (web-expert / web-supplier) hem mobil (Expo / React
// Native — mobile-manager / mobile-supplier) tarafından paylaşılır. Kalıcı
// depolama platforma göre değişir: web IndexedDB (idb), mobil `expo-secure-store`.
// Bu modül platformdan BAĞIMSIZ bir sözleşme (`KVStorage`) tanımlar; her uygulama
// kendi impl'ini giriş noktasında **enjekte eder** (`configureAuthStorage` /
// `configureCultureStorage`). Enjeksiyon yoksa süreç-içi (in-memory) yedek
// kullanılır — SSR / test / erken erişim güvenli kalır (sessiz çökme yok,
// yalnız kalıcılık olmaz).

/**
 * Anahtar-değer depolama sözleşmesi. Zustand `StateStorage` ile uyumludur
 * (senkron veya Promise dönebilir); web idb ve mobil secure-store'un ikisi de
 * async olduğundan Promise dönüşü birinci sınıf desteklenir.
 */
export interface KVStorage {
  getItem(name: string): string | null | Promise<string | null>;
  setItem(name: string, value: string): void | Promise<void>;
  removeItem(name: string): void | Promise<void>;
}

/** Enjeksiyon öncesi/olmadan güvenli yedek — oturumluk, kalıcılık yok. */
function createMemoryStorage(): KVStorage {
  const map = new Map<string, string>();
  return {
    getItem: (name) => map.get(name) ?? null,
    setItem: (name, value) => {
      map.set(name, value);
    },
    removeItem: (name) => {
      map.delete(name);
    },
  };
}

const memory = createMemoryStorage();

let authStorage: KVStorage | null = null;
let cultureStorage: KVStorage | null = null;

/** Auth token kalıcılığı impl'ini enjekte eder (web idb / mobil secure-store). */
export function configureAuthStorage(storage: KVStorage): void {
  authStorage = storage;
}

/** Aktif auth depolaması (enjekte edilmemişse in-memory yedek). */
export function getAuthStorage(): KVStorage {
  return authStorage ?? memory;
}

/**
 * Dil tercihi kalıcılığı impl'ini enjekte eder (opsiyonel). Web `localStorage`'ı
 * doğrudan kullanır (enjeksiyon gerekmez); mobil bir KV enjekte eder.
 */
export function configureCultureStorage(storage: KVStorage): void {
  cultureStorage = storage;
}

/** Enjekte edilmiş dil depolaması (yoksa `null` → i18n `localStorage`'a düşer). */
export function getCultureStorage(): KVStorage | null {
  return cultureStorage;
}
